// ROW 2 — UNIVERSAL ("the thing on the page") as DATA. CONCEPT §1, R0;
// R3 SECTION RECOMPOSITION 2026-07-20 (owner: "completely rework the second
// 'standard' row … sections that are labelled and separated with a |").
//
// Identical for every object type, forever — and rendered ONLY while a
// selection exists (R2.5 law, unchanged). The row now reads as FIVE LABELED
// SECTIONS split by visible vertical rules (`dividers: true` — RibbonRow
// renders a real | element between groups; the group caption IS the section
// label):
//
//   STYLE | EFFECTS | DEPTH | ARRANGE | OBJECT | POSITION
//
// EFFECTS (added 2026-07-20, owner: "we have enough space to add the other
// effects like shadows, echo, glow and outline as icons in the ribbon too") —
// the full effect family as SPLIT icon buttons, the Bevel/3D grammar
// reused: face = the tile-tap toggle (apply defaults / remove — one entry),
// corner chip = the def's params. The eight faces are each effect's own
// `effectDefs.tilePreview()` CSS on an `Aa` span (EffectTileFace) — ONE source
// with the drawer's Style-tab tiles, so they never drift.
//
// UNIVERSAL EFFECTS (U2, owner 2026-07-21 "apply all the same effects to shapes
// and icons"): each face gates PER KIND against lib/fx/capabilityMatrix — the
// mount computes `fxOff_<id>` / `fxOffTitle_<id>` mirrors from
// effectCapability(kind, id).mode. Enabled faces run the KIND-ROUTED verb
// (RibbonMount: text keeps applyTextEffect/removeTextEffect verbatim; shape/
// icon route the generic applyEffect/removeEffect). active-lit reads the DUAL
// bag (objProps.effects). The Outline face is HIDDEN for shapes (hideWhen —
// the Border tool owns shape edges, no twins) but present + fx-gated for icons.
// Hollow/Trace/Sticker still fold under an active 3D bake (the reason the live
// tooltip names when it applies).
//
// What changed under the owner's seven directives:
//   · Color moved HERE from the text row's Row 1 (directive §1) — same
//     ColorFlyoutPanel, still selection-aware for text (per-char via the
//     pickSolid routing law); for shapes it's the fill, for icons the tint;
//     photos are honestly disabled (a color button on a photo is a lie).
//   · The two color buttons + width field became ONE quiet Outline flyout
//     (swatches + width slider + "No outline") — the confusion §1 named.
//   · Opacity is a button + slider flyout now (§2) — the inline mini-slider
//     is gone; canonical home stays Row 2, just folded.
//   · Effects button REMOVED (§3) — the drawer auto-opens on text selection
//     and every flyout footer still deep-links; shadow/glow live in the
//     drawer + styles.
//   · Bevel and 3D are SPLIT flyout buttons (§4): face = the checkbox
//     semantics (toggle at engine defaults 50/55, active-lit), corner chip =
//     the tuning flyout (drawer keeps sides/light presets).
//   · Arrange is ONE button + flyout (§5): layer moves, align to page,
//     group/ungroup when applicable.
//   · Styles★ moved HERE from Row 1 (§7 — universal styles for shapes/icons
//     coming; until then non-text is disabled with an honest tooltip).
//
// Format painter (R4 shell) parks INSIDE the Object section — dimmed via
// disabledWhen, no longer its own group.
// Sacrifice order: Arrange(1) → Effects(2) → Depth(3) → Style(4); Object and
// Position never fold (the folding-must-help guard usually spares one-button
// Arrange anyway — its labeled chip is wider than the button). Effects folds
// after Arrange but before Depth (owner) — an eight-face section is the widest
// optional folder, so it yields early once width is tight.

const universalRow2 = {
  id: 'universal-row2',
  row: 2,
  dividers: true, // RibbonRow: a visible | rule between every section
  groups: [
    {
      id: 'style',
      label: 'Style',
      drawerSection: null, // each flyout carries its own footer link
      collapsePriority: 4, // last to fold (Arrange → Effects → Depth → Style)
      dot: 'hasTextStyle',
      controls: [
        // THE color button (moved from Row 1). Face = the effective color /
        // recipe swatch; panel = Color/Gradient/Texture tabs for text,
        // Color-only for shapes and icons (recipes are text fills today —
        // tabs that no-op would lie). The mount routes object.pickColor by
        // kind: text → per-char/whole-object law, shape → changeFillColor,
        // icon → changeIconColor.
        {
          id: 'objColor', kind: 'colorflyout', label: 'Color',
          read: 'objColor', recipeRead: 'objFillRecipe',
          handler: 'object.pickColor', recipeHandler: 'object.setFillRecipe',
          solidOnlyWhen: 'colorSolidOnly',
          disabledWhen: 'colorUnavailable',
          disabledTitle: 'Photos keep their own colors — use the photo tools',
          drawerSection: 'fill', commit: 'soon', fallback: '#000000',
        },
        // ONE Outline flyout replacing outlineColor + outlineWidth (setBorder
        // rule unchanged underneath): swatches + custom color, width slider
        // ('defer' ticks + ONE commit on release), and a "No outline" row.
        {
          id: 'outline', kind: 'outlineflyout', label: 'Outline', icon: 'outline',
          read: 'outlineWidth', colorRead: 'outlineColor',
          handler: 'object.setOutlineWidth', colorHandler: 'object.setOutlineColor',
          clearHandler: 'object.clearOutline',
          activeWhen: { read: 'outlineWidth', gt: 0 },
          min: 0, max: 100, step: 1, commit: 'defer',
          drawerSection: 'outline', fallback: 0,
        },
        // Opacity — button + slider flyout (owner §2). Still THE canonical
        // home (no-twins law); the flyout slider defers + commits ONE entry.
        {
          id: 'opacity', kind: 'flyoutsliders', label: 'Opacity', icon: 'opacity',
          activeWhen: 'opacityBelowFull',
          sliders: [
            {
              id: 'opacitySlider', label: 'Opacity', read: 'opacity',
              min: 0, max: 1, step: 0.01, handler: 'object.setOpacity',
              fallback: 1, format: (v) => `${Math.round((Number.isFinite(v) ? v : 1) * 100)}%`,
            },
          ],
        },
        // Styles★ (moved from Row 1 — §7). Text opens the full styles explorer;
        // shapes/icons open the kind-aware Styles tab (curated shape/icon
        // recipes applied via editor.applyStyle — the mount routes by kind).
        // Photos keep their own look (filter tools), so the button is honestly
        // disabled for them; the rich 273-style explorer for shapes is debt.
        {
          id: 'stylesOpen', kind: 'button', label: 'Styles', icon: 'styles', text: 'Styles★',
          handler: 'ui.openStylesExplorer',
          disabledWhen: 'stylesUnavailable',
          disabledTitle: 'Photos keep their own look — use the photo tools',
        },
      ],
    },
    {
      id: 'effects',
      label: 'Effects',
      drawerSection: null, // each effect flyout carries its own footer link
      collapsePriority: 2, // after Arrange(1), before Depth(3) — owner
      dot: 'hasFxSlot', // any live slot effect lights the section dot
      controls: [
        // SPLIT effect buttons (kind 'effectflyout' — the Bevel/3D grammar):
        // face = toggle at the def defaults / remove (fx.toggleEffect(id), one
        // entry, active-lit from the DUAL slot mirror); chip = the def's params
        // (fx.applyParams). effectId is an EFFECT_DEFS key; the face art is that
        // def's tilePreview() (ONE source with the drawer tiles). Each face
        // gates PER KIND against the capability matrix (fxOff_<id> /
        // fxOffTitle_<id>, computed by the mount): a physically-impossible cell
        // (Hollow on a photo) disables with its plain-words reason; the three
        // that fight a 3D bake also fold under is3dActive, and the live tooltip
        // (disabledTitleFrom) names whichever reason applies.
        //
        // Shadow slot family (mutually-exclusive by the engine — lighting one
        // dims the last; slot honesty comes free from the slot verbs).
        {
          id: 'fxShadow', kind: 'effectflyout', effectId: 'shadow', label: 'Shadow',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_shadow', disabledTitleFrom: 'fxOffTitle_shadow',
          disabledTitle: 'Not available for this object',
        },
        {
          id: 'fxLift', kind: 'effectflyout', effectId: 'lift', label: 'Lift',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_lift', disabledTitleFrom: 'fxOffTitle_lift',
          disabledTitle: 'Not available for this object',
        },
        {
          id: 'fxGlow', kind: 'effectflyout', effectId: 'glow', label: 'Glow',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_glow', disabledTitleFrom: 'fxOffTitle_glow',
          disabledTitle: 'Not available for this object',
        },
        {
          id: 'fxEcho', kind: 'effectflyout', effectId: 'echo', label: 'Echo',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_echo', disabledTitleFrom: 'fxOffTitle_echo',
          disabledTitle: 'Not available for this object',
        },
        // Stroke slot family. Hollow/Trace/Sticker also gate under 3D (a
        // see-through / die-cut look the extrude bake replaces); disabledTitleFrom
        // names the live reason. Outline is HIDDEN for shapes (hideWhen — the
        // Border tool owns shape edges, the no-twins law) and present for icons.
        {
          id: 'fxOutline', kind: 'effectflyout', effectId: 'outline', label: 'Outline',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          hideWhen: 'fxOutlineHidden',
          disabledWhen: 'fxOff_outline', disabledTitleFrom: 'fxOffTitle_outline',
          disabledTitle: 'Not available for this object',
        },
        {
          id: 'fxHollow', kind: 'effectflyout', effectId: 'hollow', label: 'Hollow',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_hollow', disabledTitleFrom: 'fxOffTitle_hollow',
          disabledTitle: 'Not available for this object',
        },
        {
          id: 'fxTrace', kind: 'effectflyout', effectId: 'trace', label: 'Trace letters',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_trace', disabledTitleFrom: 'fxOffTitle_trace',
          disabledTitle: 'Not available for this object',
        },
        {
          id: 'fxSticker', kind: 'effectflyout', effectId: 'sticker', label: 'Sticker',
          faceHandler: 'fx.toggleEffect', drawerSection: 'effects',
          disabledWhen: 'fxOff_sticker', disabledTitleFrom: 'fxOffTitle_sticker',
          disabledTitle: 'Not available for this object',
        },
      ],
    },
    {
      id: 'depth',
      label: 'Depth',
      drawerSection: null, // the flyout footers carry the drawer link
      collapsePriority: 3,
      dot: 'hasDepth',
      controls: [
        // SPLIT buttons (§4): face = the old checkbox semantics (toggle at
        // engine defaults, ONE entry), corner chip = the tuning flyout.
        // Bevel flyout: depth + width sliders, Rounded|Chiseled, poster sun.
        {
          id: 'bevelFly', kind: 'bevelflyout', label: 'Bevel', text: 'Bevel',
          faceHandler: 'depth.toggleBevel', activeWhen: 'bevelOn',
          drawerSection: 'depth',
        },
        // 3D flyout: depth slider, sticker plate toggle + sliders, finish
        // chips; sides/light presets stay drawer-deep (footer link). The face
        // gates against the capability matrix's `extrude` cell (threeDDisabled,
        // a DYNAMIC read) — denied for shapes/icons until the U3 lane lands the
        // silhouette source; the face un-disables itself the instant that cell
        // flips to a real mechanism (no edit here needed).
        {
          id: 'threeDFly', kind: 'threedflyout', label: '3D', text: '3D',
          faceHandler: 'depth.toggle3D', activeWhen: 'threeDOn',
          drawerSection: 'depth',
          disabledWhen: 'threeDDisabled', disabledTitleFrom: 'threeDDisabledTitle',
          disabledTitle: '3D arrives with the next update',
        },
      ],
    },
    {
      id: 'arrange',
      label: 'Arrange',
      drawerSection: 'arrange', // single flyout control → footer absorbs it
      collapsePriority: 1,
      controls: [
        // ONE button + flyout (§5). Items are one-shot actions (terminal —
        // the panel closes on pick); Group/Ungroup appear exactly when they
        // can act (showWhen mirrors — a control that sometimes cannot act
        // is a lie).
        {
          id: 'arrangeFly', kind: 'flyoutmenu', label: 'Arrange', icon: 'arrange', text: 'Arrange',
          items: [
            { id: 'fwd', label: 'Bring forward', icon: 'layerUp', handler: 'arrange.bringForward' },
            { id: 'back', label: 'Send backward', icon: 'layerDown', handler: 'arrange.sendBackward' },
            { id: 'front', label: 'Bring to front', icon: 'layerTop', handler: 'arrange.bringToFront' },
            { id: 'toBack', label: 'Send to back', icon: 'layerBottom', handler: 'arrange.sendToBack' },
            { divider: true },
            { id: 'alignL', label: 'Align to page left', icon: 'alignLeft', handler: 'arrange.alignToPage', args: ['left'] },
            { id: 'alignC', label: 'Center on page', icon: 'alignCenter', handler: 'arrange.alignToPage', args: ['centerH'] },
            { id: 'alignR', label: 'Align to page right', icon: 'alignRight', handler: 'arrange.alignToPage', args: ['right'] },
            { divider: true },
            { id: 'grp', label: 'Group selection', icon: 'group', handler: 'arrange.group', showWhen: 'canGroup' },
            { id: 'ungrp', label: 'Ungroup', icon: 'ungroup', handler: 'arrange.ungroup', showWhen: 'canUngroup' },
            // COMBINE (Depth-Everywhere wave N, lane N2): 2+ shapes → ONE plain
            // fabric.Path via exact 2D booleans. Educator words (union/subtract/
            // intersect stay out of the UI); the RULE rides each item's label —
            // Cut out is the BOTTOM shape minus the ones on top (z-order). Items
            // showWhen 'canCombineShapes' (2+ fill shapes) — the same honest
            // hide-when-it-can't-act posture as Group/Ungroup above (the flyout
            // menu grammar has no per-item disabled; showWhen IS its truth).
            { divider: true },
            { id: 'combineMerge', label: 'Merge shapes', icon: 'shapes', handler: 'arrange.combine', args: ['merge'], showWhen: 'canCombineShapes' },
            { id: 'combineCut', label: 'Cut out (bottom shape minus the ones on top)', icon: 'shapes', handler: 'arrange.combine', args: ['cut'], showWhen: 'canCombineShapes' },
            { id: 'combineOverlap', label: 'Keep only where they overlap', icon: 'shapes', handler: 'arrange.combine', args: ['overlap'], showWhen: 'canCombineShapes' },
          ],
        },
      ],
    },
    {
      id: 'object',
      label: 'Object',
      drawerSection: null,
      neverCollapse: true,
      controls: [
        // Format painter parks here (R4 shell — always dimmed until it
        // lands; disabledWhen keeps lock/dup/delete live around it).
        {
          id: 'formatPainter', kind: 'toggle', label: 'Format painter — copy this look',
          icon: 'formatPainter', handler: 'object.formatPainter',
          activeWhen: 'painterActive',
          disabledWhen: 'painterDisabled',
          disabledTitle: 'Format painter is coming soon',
        },
        // Wiring: editor.toggleLock / duplicate / delete (house tail).
        { id: 'lock', kind: 'toggle', label: 'Lock', icon: 'lock', handler: 'object.toggleLock', activeWhen: 'locked' },
        { id: 'duplicate', kind: 'button', label: 'Duplicate (Ctrl+D)', icon: 'duplicate', handler: 'object.duplicate' },
        { id: 'delete', kind: 'button', label: 'Delete', icon: 'trash', handler: 'object.delete', danger: true },
      ],
    },
    {
      id: 'position',
      label: 'Position',
      drawerSection: null,
      neverCollapse: true,
      tail: true,
      controls: [
        // Row 2 tail button (decision §6.3) — opens the drawer's Position
        // panel; the button IS the drawer link, so no separate footer.
        { id: 'positionOpen', kind: 'button', label: 'Position', icon: 'position', text: 'Position', handler: 'ui.openPositionPanel' },
        // The panel toggle (owner 2026-07-23: the drawer never auto-opens —
        // "only show that bar if a user clicks on an advanced button").
        // Lit while the panel is open; click toggles.
        { id: 'advancedPanel', kind: 'toggle', label: 'More options panel', icon: 'filters', handler: 'ui.togglePanel', activeWhen: 'panelOpen' },
      ],
    },
  ],
};

export default universalRow2;
